import { useFormikContext } from "formik";
import { useContext, useEffect, useMemo } from "react";
import { useAppSelector, usePermissions } from "@app/hooks";
import { FEModules } from "@utils/moduleList";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetForestDisturbances,
	useCreateForestDisturbanceMutation,
	useDeleteForestDisturbanceMutation,
} from "@hooks/index";
import type {
	ForestDisturbanceCreatePayload,
	ForestDisturbanceData,
	ModuleFull,
} from "./forestManagementTypes";
import type {
	ModuleProps,
	T2Schema,
	TranslationKey,
} from "@typedefs/modulesInterfaces";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { useContextAwareGetLandUseChange } from "@hooks/index";
import { mergeLocalAndServerChanges } from "../inputs/utils";
import { ForestManagementSections } from "./forestManagement";
import { handleDisableSww } from "../moduleUtils";
import { TranslatedFormSubmodule } from "@components/formSubmodule/TranslatedFormSubmodule";
import { useProcessedModuleSchema } from "./useProcessedModuleSchema";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import ForestContext from "@contexts/ForestContext";
import GenericModule from "../GenericModule";
import GenericOptionalSubModule from "../GenericOptionalSubmodule";

interface ForestManagementTierOneProps {
	initValues: ModuleFull;
	t2Schema: T2Schema;
	setInitValues: React.Dispatch<React.SetStateAction<ModuleFull>>;
}

const FOREST_CONFIG = {
	disturbances: {
		fieldName: "disturbances" as const,
		translationKey: "module.add_disturbance" as const,
		payloadType: "ForestDisturbanceCreatePayload" as const,
	},
} as const;

const ForestManagementTierOne = ({
	initValues,
	setInitValues,
	t2Schema,
	...moduleProps
}: ModuleProps & ForestManagementTierOneProps) => {
	const { values } = useFormikContext<ModuleFull>();
	const { activeActivityId, activity, project } = useAppSelector(selectCurrentProject);
	const { isReadOnly } = usePermissions();
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const lucId = moduleList.find((module) => module.id === FEModules.LandUseChange)?.uniqueId;
	const forestContext = useContext(ForestContext)
	const parentId = moduleList.find((m) => m.id === FEModules.ForestManagement)?.uniqueId;

	// Use the custom hook to process the module schema
	const { degradation, processedModuleSchema } = useProcessedModuleSchema(
		moduleProps.moduleSchema,
		activity,
		project,
		forestContext,
		isReadOnly
	);

	const [deleteDisturbance, { isLoading: isLoadingDeleteDisturbance }] = useDeleteForestDisturbanceMutation();
	const [addDisturbance, { isLoading: isLoadingAddDisturbance }] = useCreateForestDisturbanceMutation();

	const createRemoveHandler = () => (
		remove: <T>(index: number) => T | undefined,
		index: number,
		id?: number | null,
	) => {
		remove(index);

		if (id) {
			if (deleteDisturbance) {
				deleteDisturbance({
					id,
					activityId: activeActivityId ?? 0,
					parent: parentId ?? 0,
				});
			}
		}
	};

	const createAddHandler = () => async () => {
		const payload: ForestDisturbanceCreatePayload = {
			body: { parent: parentId ?? 0 },
			activityId: activeActivityId ?? 0,
		};
		try {
			await addDisturbance(payload).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	const translationKeySubmodule = (name: string): TranslationKey => {
		const config = FOREST_CONFIG[name as keyof typeof FOREST_CONFIG];
		return config ? config.translationKey : "module.add_disturbance"; // fallback
	};

	/* ADD DISTURBANCES LOGIC */
	const { data: disturbancesData, isLoading: isLoadingDisturbances } = useContextAwareGetForestDisturbances({ activityId: activeActivityId ?? 0 });
	const { data: lucData } = useContextAwareGetLandUseChange(lucId ?? 0, { skip: !lucId });

	const disturbances = useMemo(() => {
		return mergeLocalAndServerChanges({
			currentChangesList: values.disturbances,
			incomingList:
				disturbancesData?.map((dist) => ({
					...dist,
					land_use_change: lucData?.module,
				})) ?? [] as ForestDisturbanceData[],
		});
	}, [disturbancesData, lucData]);

	useEffect(() => {
		setInitValues(() => ({ ...values, disturbances: disturbances }));
		if (forestContext && forestContext.setDisturbancesIds) forestContext.setDisturbancesIds(disturbancesData?.map((d) => d.id))
		// eslint-disable-next-line
	}, [disturbances, setInitValues]); // we don't want values here

	return (
		<GenericModule
			{...moduleProps}
			moduleSchema={processedModuleSchema}
			t2Schema={t2Schema}
			beModuleType={moduleProps.beModuleType}
			disableOptionals
		>
			{
				({ belongsToLuc, isSWW }) => {
					const degradationPercentage = degradation?.inputGroups?.[0];
					const curatedDegradationPercentage = degradationPercentage ? handleDisableSww({
						input: degradationPercentage,
						belongsToLuc,
						isReadOnly
					}) : undefined;

					return (
						<>
							{processedModuleSchema.optionalSubmodules?.map((subModule, index) => {
								return (
									<GenericOptionalSubModule<ForestDisturbanceData>
										key={`${subModule.name}-${index}`}
										optionalSchema={subModule}
										values={initValues.disturbances}
										createHandler={createAddHandler()}
										removeHandler={createRemoveHandler()}
										fieldArrayName={subModule.name}
										addLabel={translationKeySubmodule(subModule.name)}
										belongsToLuc={belongsToLuc}
										isSWW={isSWW}
										isReadOnly={isReadOnly}
										isOptionalLoading={isLoadingDisturbances}
										isCreateLoading={isLoadingAddDisturbance}
										isDeleteLoading={isLoadingDeleteDisturbance}
									/>
								);
							})}

							{/* DEGRADATION */}
							{curatedDegradationPercentage && (
								<TranslatedFormSubmodule submoduleName={ForestManagementSections.DEGRADATION} key={"Degradation"}>
									<div className="py-2">
										<GenericInputGroup inputGroup={{ ...curatedDegradationPercentage }} />
									</div>
								</TranslatedFormSubmodule>
							)}
						</>
					);
				}
			}
		</GenericModule>
	);
};

export default ForestManagementTierOne;